type BreakpointKeys = 'sm' | 'md' | 'xl';
export type TResponsiveValue<T> = T | Partial<Record<BreakpointKeys, T>>;

const BREAKPOINTS: BreakpointKeys[] = ['sm', 'md', 'xl'];

const capitalize = (str: string): string => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

const cacheMap = new Map<string, string>();

export const isResponsiveObject = <T>(value: TResponsiveValue<T> | undefined): value is Partial<Record<BreakpointKeys, T>> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export const buildResponsiveStyles = (
  value: TResponsiveValue<string | number> | undefined,
  prefix: string,
): Record<string, string | number> => {
  if (!value || !isResponsiveObject(value)) return {};

  const vars: Record<string, string | number> = {};

  BREAKPOINTS.forEach((bp) => {
    const bpValue = value[bp];
    if (bpValue !== undefined) {
      vars[`--${ prefix }-${ bp }`] = typeof bpValue === 'number' ? `${ bpValue }px` : bpValue;
    }
  });

  return vars;
};

export const generateResponsiveClasses = (
  value: TResponsiveValue<any> | undefined,
  prefix: string,
  styles: Record<string, string>,
  transform?: (val: any) => string,
): string => {
  if (value === undefined || value === null) return '';

  if (typeof value === 'number' || typeof value === 'string') {
    const cacheKey = `${ prefix }-${ value }-${ transform?.name || 'none' }`;

    if (cacheMap.has(cacheKey)) {
      return cacheMap.get(cacheKey)!;
    }

    const transformedValue = transform ? transform(value) : value;
    const result = styles[`${ prefix }${ transformedValue }`] || '';

    if (result) {
      cacheMap.set(cacheKey, result);
    }

    return result;
  }

  return BREAKPOINTS
    .map((breakpoint) => {
      const breakpointValue = value[breakpoint];
      if (breakpointValue === undefined || breakpointValue === null) return '';

      const transformedValue = transform ? transform(breakpointValue) : breakpointValue;
      return styles[`${ prefix }${ capitalize(breakpoint) }${ transformedValue }`] || '';
    })
    .filter(Boolean)
    .join(' ');
};

export { capitalize, BREAKPOINTS };
export type { BreakpointKeys };
